import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { api } from '../lib/api';
import { buildSignUpUrl } from '../lib/cognitoUrls';
import { useAuthStore } from '../store/authStore';
import {
  deriveOwnerProgress,
  OWNER_STEP_COPY,
  OwnerCta,
  OwnerProgress,
  OwnerProgressListing,
} from '../lib/ownerProgress';
import Button from '../components/Button';
import Card from '../components/Card';
import SectionHeading from '../components/SectionHeading';
import UseCaseGrid from '../components/UseCaseGrid';
import OwnerProgressTracker from '../components/OwnerProgressTracker';

function BenefitIcon({ path }: { path: string }) {
  return (
    <svg
      className="h-8 w-8 text-primary-600"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
    </svg>
  );
}

const BENEFITS = [
  {
    title: 'Earn from what\'s idle',
    body: 'Turn unused spaces, equipment, or time into revenue on your schedule.',
    path: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  {
    title: 'Paid out automatically',
    body: 'Stripe Connect verifies you once, then sends booking money to your bank.',
    path: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  },
  {
    title: 'Availability that matches reality',
    body: 'Set your own days, times, and prices so you only take bookings you can honor.',
    path: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  {
    title: 'Your own link and QR code',
    body: 'Every listing gets a unique URL and a QR code you can print or share.',
    path: 'M13 10V3L4 14h7v7l9-11h-7z',
  },
];

const MCP_CARDS = [
  'Create a listing from a description',
  'Open availability by conversation',
  'Ask what\'s booked this week',
];

const FAQ = [
  {
    question: 'What can I list?',
    answer:
      'Parking, tools, equipment, space, or your time — anything people rent by the hour, day, week, or month.',
  },
  {
    question: 'Is my information safe?',
    answer:
      'Yes. Stripe collects and stores your bank and identity details. Pay2Rent.me never sees your account numbers.',
  },
  {
    question: 'When do I get paid?',
    answer:
      'Stripe pays out to your bank on a rolling schedule once your account is active.',
  },
  {
    question: 'What does it cost?',
    answer:
      'Nothing monthly, nothing to list. You keep 95% of every booking — about 3% goes to card processing and 2% to Pay2Rent.me.',
  },
  {
    question: 'Can I use AI to manage my listings?',
    answer:
      'Yes. Plug Claude, Cursor, or ChatGPT into Pay2Rent.me to list, price, and manage availability in plain English.',
  },
  {
    question: 'Do I need my own website?',
    answer: 'No. Every listing gets its own URL and a QR code.',
  },
];

const BecomeOwnerPage: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshProfile } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProgress, setIsLoadingProgress] = useState(auth.isAuthenticated);
  const [connect, setConnect] = useState<{
    connected: boolean;
    onboarded: boolean;
  } | null>(null);
  const [listings, setListings] = useState<OwnerProgressListing[] | null>(null);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      setIsLoadingProgress(false);
      return;
    }
    loadProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, location.search]);

  const loadProgress = async () => {
    setIsLoadingProgress(true);
    try {
      const [connectResult, listingsResult] = await Promise.allSettled([
        api.get<{ connected: boolean; onboarded: boolean; accountId?: string }>(
          '/payments/stripe/connect/status'
        ),
        api.get<{ listings: OwnerProgressListing[] }>('/listings/my/listings'),
      ]);

      const nextConnect =
        connectResult.status === 'fulfilled'
          ? {
              connected: connectResult.value.connected,
              onboarded: connectResult.value.onboarded,
            }
          : null;
      setConnect(nextConnect);

      if (listingsResult.status === 'fulfilled') {
        setListings(listingsResult.value.listings);
      } else {
        setListings([]);
      }

      if (nextConnect?.onboarded) {
        if (!user?.isOwner) {
          await refreshProfile();
        }
        if (location.search !== '') {
          toast.success('Your Stripe account is active!');
        }
      }
    } finally {
      setIsLoadingProgress(false);
    }
  };

  const progress: OwnerProgress = deriveOwnerProgress({
    isAuthenticated: auth.isAuthenticated,
    connect,
    listings,
  });

  const handleGetStarted = async () => {
    try {
      setIsLoading(true);
      const response = await api.post<{ url: string }>(
        '/payments/stripe/connect/create-link'
      );

      if (response.url) {
        window.location.href = response.url;
      }
    } catch {
      toast.error('Failed to start onboarding. Please try again.');
      setIsLoading(false);
    }
  };

  const handleCta = (cta: OwnerCta) => {
    if (cta.kind === 'signup') {
      window.location.href = buildSignUpUrl();
      return;
    }
    if (cta.kind === 'stripe') {
      handleGetStarted();
      return;
    }
    if (cta.to) {
      navigate(cta.to);
    }
  };

  const ctaLabel = isLoading ? 'Redirecting to Stripe...' : progress.cta.label;

  return (
    <>
      <Helmet>
        <title>Become an Owner - Pay2Rent.me</title>
        <meta
          name="description"
          content="List a space, a machine, or an hour of your time. Set your own schedule and your own price."
        />
      </Helmet>

      <div className="space-y-16">
        <section className="text-center py-12 sm:py-16">
          <p className="text-sm font-semibold uppercase tracking-wide text-primary-600 mb-3">
            For owners
          </p>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            {auth.isAuthenticated
              ? 'Finish setting up your rentals'
              : 'Turn what you own into income'}
          </h1>
          {!auth.isAuthenticated && (
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              List a space, a machine, or an hour of your time. Set your own schedule
              and your own price.
            </p>
          )}
          {auth.isAuthenticated && (
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Pick up where you left off — payouts, your first listing, then
              availability.
            </p>
          )}
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              onClick={() => handleCta(progress.cta)}
              disabled={isLoading}
            >
              {ctaLabel}
            </Button>
            {!auth.isAuthenticated && (
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center px-6 py-3 text-lg font-medium rounded-lg text-gray-700 hover:bg-gray-100"
              >
                See how it works
              </a>
            )}
          </div>
        </section>

        {auth.isAuthenticated && (
          <section>
            {connect?.connected && !connect.onboarded && (
              <div className="bg-accent-50 border border-accent-200 rounded-2xl p-6 mb-8">
                <h3 className="text-lg font-semibold text-accent-700 mb-2">
                  Complete Your Stripe Setup
                </h3>
                <p className="text-accent-700 mb-3">
                  You started the onboarding process but haven&apos;t finished yet.
                  Click the button below to continue where you left off and complete
                  your Stripe account setup.
                </p>
                <p className="text-accent-600 text-sm">
                  You&apos;ll need to provide some business information and banking
                  details to receive payments.
                </p>
              </div>
            )}
            <OwnerProgressTracker
              progress={progress}
              isLoading={isLoadingProgress}
            />
          </section>
        )}

        {!auth.isAuthenticated && (
          <section id="how-it-works">
            <SectionHeading eyebrow="Getting started" title="How it works" />
            <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {OWNER_STEP_COPY.map((step, index) => (
                <Card key={step.id}>
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-lg font-bold text-primary-600">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600">{step.description}</p>
                </Card>
              ))}
            </div>
          </section>
        )}

        <section>
          <SectionHeading eyebrow="Use cases" title="What owners rent out" />
          <div className="mt-10">
            <UseCaseGrid />
          </div>
        </section>

        <section>
          <Card padding="lg" className="text-center max-w-3xl mx-auto">
            <SectionHeading title="One fee. Nothing else." />
            <p className="mt-8 text-6xl font-extrabold text-gray-900">5%</p>
            <p className="mt-2 text-lg text-gray-600">all-in, per booking</p>
            <div className="mt-6 space-y-1 text-gray-700">
              <p>~3% card processing</p>
              <p>2% to Pay2Rent.me</p>
            </div>
            <ul className="mt-8 grid gap-2 text-left max-w-sm mx-auto text-gray-700">
              {[
                'No monthly fee',
                'No listing fee',
                'No fee when nothing books',
                'Paid out straight to your bank',
              ].map(item => (
                <li key={item} className="flex items-start">
                  <span className="text-primary-600 mr-2">✓</span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-gray-500">
              Card processing is Stripe&apos;s standard 2.9% + 30¢ per transaction.
            </p>
          </Card>
        </section>

        <section>
          <SectionHeading
            eyebrow="AI-native"
            title="Manage your rentals by just asking"
            subtitle="Pay2Rent.me runs an MCP server, so you can connect your account to Claude, Cursor, ChatGPT, or any MCP-compatible assistant. Create listings, adjust pricing, open and close availability, and check what's booked — in plain language, no dashboard required."
          />
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {MCP_CARDS.map(title => (
              <Card key={title}>
                <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link
              to="/connect"
              className="font-medium text-primary-600 hover:text-primary-700"
            >
              See how to connect
            </Link>
          </div>
        </section>

        <section>
          <SectionHeading title="Why owners list here" />
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {BENEFITS.map(benefit => (
              <Card key={benefit.title}>
                <BenefitIcon path={benefit.path} />
                <h3 className="mt-4 text-lg font-semibold text-gray-900">
                  {benefit.title}
                </h3>
                <p className="mt-2 text-gray-600">{benefit.body}</p>
              </Card>
            ))}
          </div>
        </section>

        <section>
          <SectionHeading title="FAQs" />
          <dl className="mt-10 grid gap-6 md:grid-cols-2">
            {FAQ.map(item => (
              <Card key={item.question}>
                <dt className="text-lg font-semibold text-gray-900">{item.question}</dt>
                <dd className="mt-2 text-gray-600">{item.answer}</dd>
              </Card>
            ))}
          </dl>
        </section>

        <section className="bg-primary-600 text-white rounded-2xl p-12 text-center">
          <h2 className="text-3xl font-bold mb-4">
            {auth.isAuthenticated
              ? 'Keep going — you are almost live'
              : 'Ready to start earning?'}
          </h2>
          <p className="text-xl mb-8 opacity-90">
            {auth.isAuthenticated
              ? 'Finish the next step and your listing can start taking bookings.'
              : 'Create a free account and list in minutes.'}
          </p>
          <Button
            size="lg"
            variant="secondary"
            onClick={() => handleCta(progress.cta)}
            disabled={isLoading}
          >
            {ctaLabel}
          </Button>
        </section>
      </div>
    </>
  );
};

export default BecomeOwnerPage;
